<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, contact ? 'device-active--heat' : '']"
      :style="{ '--pin-active-color': HEAT_ACCENT }"
      :title="device.deviceName">
      <ControlStatusRing :value="highestDemand" :accent="HEAT_ACCENT">
        <ControlIcon :name="controlIcon" :size="pinIconSize" :style="contact ? { color: HEAT_ACCENT } : {}" />
      </ControlStatusRing>
      <div v-if="contact" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- ── Card mode ─────────────────────────────────────────────────────── -->
  <!-- The controller as one lamp: the flame sits in the circle the boiler's own
       contact lights, the ring around it carries the highest demand any zone is
       asking for, and the largest size carries the class's one settable row. -->
  <template v-else-if="mode === 'card'">
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="contact && state.enabled"
      :accent="HEAT_ACCENT"
      class="heat-card-face"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <div
          class="heat-card-dial"
          :class="{ 'heat-card-dial--on': contact, 'heat-card-dial--blocked': !state.enabled }">
          <ControlStatusRing :value="highestDemand" :accent="state.enabled ? HEAT_ACCENT : DIM_ACCENT">
            <span class="heat-card-dial__disc">
              <ControlIcon :name="controlIcon" />
            </span>
          </ControlStatusRing>
        </div>
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="heat-card-face__value" :class="{ 'heat-card-face__value--on': contact && state.enabled }">
          {{ cardStateWord }}
        </span>
      </template>

      <template v-if="cardFacts.length" #facts>
        <span v-for="fact in cardFacts" :key="fact" class="heat-card-face__fact">{{ fact }}</span>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <button
          type="button"
          class="heat-card-face__switch"
          :class="{ 'heat-card-face__switch--blocked': !state.enabled }"
          :disabled="controlling"
          @click.stop="toggleEnabled">
          <span class="material-symbols-outlined">{{ state.enabled ? 'power_settings_new' : 'power_off' }}</span>
          {{ state.enabled ? tt('Block', 'Prepreči') : tt('Allow', 'Dovoli') }}
        </button>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ─────────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'local_fire_department'"
      :name="displayName"
      :is-on="contact"
      active-color="orange-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- The controller's own sheet: the contact it drives, the furnace switch,
       and one row per zone — its real name (from the thermostat the pick
       points at), its reading, and the band the handler acts on. A generic
       switch card could show none of that. -->
  <template v-else>
    <div class="heat-sheet">
      <div class="heat-hero">
        <button
          type="button"
          class="heat-circle"
          :class="{ 'heat-circle--on': contact, 'heat-circle--blocked': !state.enabled }"
          :disabled="controlling"
          :title="
            state.enabled
              ? tt('Block heat demand', 'Prepreči potrebo po toploti')
              : tt('Allow heat demand', 'Dovoli potrebo po toploti')
          "
          @click="toggleEnabled">
          <ControlIcon :name="controlIcon" size="2.4rem" />
        </button>
        <div class="heat-state" :class="{ 'heat-state--on': contact, 'heat-state--blocked': !state.enabled }">
          {{ stateWord }}
        </div>
        <div class="heat-caption">{{ caption }}</div>
      </div>

      <p class="heat-band">
        {{
          tt(
            `Raises at ${formatPercent(thresholds.threshold)} · releases below ${formatPercent(thresholds.releaseAt)}`,
            `Vklop nad ${formatPercent(thresholds.threshold)} · izklop pod ${formatPercent(thresholds.releaseAt)}`,
          )
        }}
      </p>

      <div v-if="zones.length === 0" class="heat-empty">
        {{ tt('No zone demand available on this controller.', 'Na tem krmilniku ni na voljo podatkov o conah.') }}
      </div>

      <ul v-else class="heat-zones">
        <li
          v-for="zone in zones"
          :key="zone.demandKey"
          class="heat-zone"
          :class="{
            'heat-zone--asking': zone.asking,
            'heat-zone--held': !zone.asking && isHeld(zone),
            'heat-zone--silent': zone.demand === null,
            'heat-zone--window': zone.windowOpen,
          }">
          <div class="heat-zone__head">
            <span class="heat-zone__name" :class="{ 'heat-zone__name--placeholder': !zone.named }">
              {{ zone.name }}
            </span>
            <span class="heat-zone__value">
              {{ zone.demand === null ? '—' : formatPercent(zone.demand) }}
            </span>
          </div>
          <div class="heat-rail" role="img" :aria-label="`${zone.name}: ${zone.demand ?? 0} %`">
            <span class="heat-rail__band" :style="bandStyle" />
            <span
              class="heat-rail__fill"
              :style="{ width: `${zone.demand === null ? 0 : clampPercent(zone.demand)}%` }" />
            <span class="heat-rail__mark" :style="markStyle" />
          </div>
          <div class="heat-zone__meta">
            <span class="heat-zone__word">{{ zoneWord(zone) }}</span>
            <span v-if="zone.detail" class="heat-zone__detail">{{ zone.detail }}</span>
          </div>
        </li>
      </ul>

      <p v-if="noSourceNote" class="heat-note heat-note--warn">{{ noSourceNote }}</p>
      <p v-if="!state.enabled" class="heat-note">
        {{
          tt(
            'The switch is off, so the contact cannot rise.',
            'Stikalo je izklopljeno, zato se kontakt ne more vklopiti.',
          )
        }}
      </p>
      <p v-if="guardNote" class="heat-note">{{ guardNote }}</p>

      <SetpointTimeline
        :device-values="device.deviceValues ?? []"
        :device-id="device.id"
        accent-color="rgb(249, 115, 22)" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { CONTROL_ICONS } from '../device-icons/controlIcons';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import DeviceCardShell from './DeviceCardShell.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import SetpointTimeline from './SetpointTimeline.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { useEntitiesStore } from '@/stores/entities';
import { tt } from '../../utils/consoleText';
import {
  heatDemandState,
  heatDemandThresholds,
  heatDemandZones,
  type HeatDemandSource,
  type HeatDemandZone,
} from '../../utils/heatDemandZones';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  colSpan?: number;
  rowSpan?: number;
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [deviceId: string, newValue: unknown];
}>();

/** The controller's own accent — the fire it decides about. */
const HEAT_ACCENT = 'rgb(249, 115, 22)';
/** The same tone with the heat taken out, for a controller told to stay out. */
const DIM_ACCENT = 'rgb(148, 163, 184)';

/**
 * The glyph the drawn surfaces show. `device.icon` is a Material Symbols name,
 * and the drawn set only knows its own keys (`local_fire_department` is not one
 * of them), so an unmapped class icon would render as a blank slot — the pin and
 * the sheet's circle would paint a full orange disc with nothing in it.
 */
const controlIcon = computed(() => {
  const icon = String(props.device.icon ?? '');
  return Object.prototype.hasOwnProperty.call(CONTROL_ICONS, icon) ? icon : 'flame';
});

const entitiesStore = useEntitiesStore();
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (rows) => {
    if (rows?.length) initializeValues(rows);
  },
  { deep: true },
);

/** The device's readings: the socket's live map over the row's last known ones. */
const values = computed<Record<string, unknown>>(() => {
  const raw: Record<string, unknown> = {};
  for (const row of props.device.deviceValues ?? []) {
    const key = String(row.valueKey ?? '');
    if (key) raw[key] = row.currentValue;
  }
  Object.assign(raw, currentValues.value);

  // A handler device's rows can be stored prefixed (`heat_demand_controller__
  // demand1`); the zone resolver reads the plain key the class declares.
  const plain: Record<string, unknown> = { ...raw };
  for (const [key, reading] of Object.entries(raw)) {
    const separator = key.indexOf('__');
    if (separator > 0) plain[key.slice(separator + 2)] = reading;
  }
  return plain;
});

const thresholds = computed(() => heatDemandThresholds(props.device.settings));

/**
 * valueId → the device and value behind it, so a zone can carry the name the
 * operator gave its thermostat. Built once per device-list change rather than
 * per zone row.
 */
const valueIndex = computed(() => {
  const index = new Map<string, HeatDemandSource>();
  for (const device of entitiesStore.devices) {
    const rows = entitiesStore.getDeviceValueRows(device) ?? [];
    if (rows.length === 0) continue;
    const deviceName = (device.shortName || device.deviceName || '').trim();
    for (const row of rows) {
      const id = String(row.id ?? '');
      if (!id) continue;
      index.set(id, { deviceName, valueName: row.name ?? null });
    }
  }
  return index;
});

function sourceOf(valueId: string): HeatDemandSource | null {
  return valueIndex.value.get(valueId) ?? null;
}

const zones = computed(() => heatDemandZones(props.device.settings, values.value, sourceOf));
const state = computed(() => heatDemandState(zones.value, values.value, thresholds.value));
const contact = computed(() => state.value.contact);

const highestDemand = computed(() => {
  const readings = zones.value.map((zone) => zone.demand).filter((value): value is number => value !== null);
  return readings.length > 0 ? Math.max(...readings) : null;
});

const stateWord = computed(() => {
  if (!state.value.enabled) return tt('Blocked', 'Preprečeno');
  return contact.value
    ? tt('Heat demand ON', 'Potreba po toploti VKLOP')
    : tt('Heat demand OFF', 'Potreba po toploti IZKLOP');
});

const caption = computed(() => {
  const total = zones.value.length;
  if (total === 0) return tt('No zone demand right now', 'Trenutno ni potreb po conah');
  const asking = state.value.askingCount;
  if (contact.value) {
    return tt(
      `${state.value.activeCount} of ${total} zones holding`,
      `${state.value.activeCount} od ${total} con drži`,
    );
  }
  return tt(`${asking} of ${total} zones asking`, `${asking} od ${total} con zahteva`);
});

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function formatPercent(value: number): string {
  return `${Math.round(value)} %`;
}

/** The handler's band, drawn where it acts. */
const bandStyle = computed(() => ({
  left: `${clampPercent(thresholds.value.releaseAt)}%`,
  width: `${Math.max(0, clampPercent(thresholds.value.threshold) - clampPercent(thresholds.value.releaseAt))}%`,
}));

const markStyle = computed(() => ({ left: `${clampPercent(thresholds.value.threshold)}%` }));

/** A zone inside the band is what a held-on contact is holding. */
function isHeld(zone: HeatDemandZone): boolean {
  return contact.value && zone.demand !== null && !zone.windowOpen && zone.demand > thresholds.value.releaseAt;
}

function zoneWord(zone: HeatDemandZone): string {
  if (zone.windowOpen) return tt('window open', 'okno odprto');
  if (zone.demand === null) return tt('no reading', 'ni odčitka');
  if (zone.asking) return tt('asking', 'zahteva');
  if (isHeld(zone)) return tt('holding', 'drži');
  return tt('idle', 'miruje');
}

const noSourceNote = computed(() => {
  if (!state.value.noSource) return '';
  const timeout = thresholds.value.noSourceTimeoutSeconds;
  if (!contact.value) {
    return tt('No zone is reporting demand.', 'Nobena cona ne poroča potrebe.');
  }
  if (timeout <= 0) {
    return tt(
      'No zone is reporting demand — the watchdog is disabled, so the contact stays as it is.',
      'Nobena cona ne poroča potrebe — nadzor je izklopljen, zato kontakt ostane tak, kot je.',
    );
  }
  const minutes = Math.max(1, Math.round(timeout / 60));
  return tt(
    `No zone is reporting demand — the contact drops after ${minutes} min.`,
    `Nobena cona ne poroča potrebe — kontakt se izklopi po ${minutes} min.`,
  );
});

const guardNote = computed(() => {
  const on = Math.round(thresholds.value.minOnSeconds / 60);
  const off = Math.round(thresholds.value.minOffSeconds / 60);
  if (on <= 0 && off <= 0) return '';
  return tt(
    `Anti-short-cycle: at least ${on} min on and ${off} min off.`,
    `Zaščita pred kratkimi cikli: najmanj ${on} min vklop in ${off} min izklop.`,
  );
});

async function toggleEnabled(): Promise<void> {
  if (controlling.value) return;
  const ok = await setProperty(props.device, 'enabled', !state.value.enabled);
  if (ok) emit('value-update', props.device.id, !state.value.enabled);
}

// ── Display ──────────────────────────────────────────────────────────────
/** The card's headline: the contact's own word, or why it cannot rise. */
const cardStateWord = computed(() => {
  if (!state.value.enabled) return tt('Blocked', 'Preprečeno');
  return contact.value ? tt('ON', 'VKLOP') : tt('OFF', 'IZKLOP');
});

/** The readings beside the headline: who is asking, and where the band sits. */
const cardFacts = computed(() => {
  const total = zones.value.length;
  const facts: string[] = [];
  if (total > 0) {
    const count = contact.value ? state.value.activeCount : state.value.askingCount;
    facts.push(tt(`${count} of ${total} zones`, `${count} od ${total} con`));
  }
  facts.push(
    tt(
      `raises at ${formatPercent(thresholds.value.threshold)}`,
      `vklop nad ${formatPercent(thresholds.value.threshold)}`,
    ),
  );
  return props.cardSize === 'mid' ? facts.slice(0, 1) : facts;
});

const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

// colSpan/rowSpan arrive like they do for the other layout-aware surfaces; the
// sheet itself is fluid.
void props.colSpan;
void props.rowSpan;
</script>

<style scoped>
.heat-sheet {
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  padding: 0.25em 0;
}

.heat-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35em;
}

.heat-circle {
  display: grid;
  place-items: center;
  width: 4.5em;
  height: 4.5em;
  border-radius: 9999px;
  border: 2px solid var(--device-color-heat, rgb(249, 115, 22));
  color: var(--device-color-heat, rgb(249, 115, 22));
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 12%, transparent);
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    opacity 0.15s ease;
}

.heat-circle--on {
  background: var(--device-color-heat, rgb(249, 115, 22));
  color: #fff;
}

.heat-circle--blocked {
  opacity: 0.45;
  border-style: dashed;
}

.heat-state {
  font-weight: 600;
  font-size: 0.95em;
  color: rgb(71, 85, 105);
}

.heat-state--on {
  color: var(--device-color-heat, rgb(249, 115, 22));
}

.heat-state--blocked {
  color: rgb(148, 163, 184);
}

.heat-caption {
  font-size: 0.8em;
  color: rgb(100, 116, 139);
}

.heat-band {
  margin: 0;
  text-align: center;
  font-size: 0.75em;
  color: rgb(100, 116, 139);
}

.heat-zones {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.heat-zone {
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  padding: 0.45em 0.6em;
  border-radius: 0.6em;
  background: rgb(248, 250, 252);
  border: 1px solid transparent;
}

.heat-zone--asking {
  border-color: var(--device-color-heat, rgb(249, 115, 22));
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 8%, transparent);
}

.heat-zone--held {
  border-color: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 45%, transparent);
}

.heat-zone--window,
.heat-zone--silent {
  opacity: 0.65;
}

.heat-zone__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5em;
}

.heat-zone__name {
  font-size: 0.85em;
  font-weight: 600;
  color: rgb(30, 41, 59);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.heat-zone__name--placeholder {
  font-weight: 500;
  color: rgb(100, 116, 139);
}

.heat-zone__value {
  font-size: 0.85em;
  font-variant-numeric: tabular-nums;
  color: rgb(51, 65, 85);
}

.heat-rail {
  position: relative;
  height: 0.35em;
  border-radius: 9999px;
  background: rgb(226, 232, 240);
  overflow: hidden;
}

.heat-rail__band {
  position: absolute;
  top: 0;
  bottom: 0;
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 22%, transparent);
}

.heat-rail__fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  border-radius: 9999px;
  background: var(--device-color-heat, rgb(249, 115, 22));
}

.heat-zone--silent .heat-rail__fill,
.heat-zone--window .heat-rail__fill {
  background: rgb(148, 163, 184);
}

.heat-rail__mark {
  position: absolute;
  top: -1px;
  bottom: -1px;
  width: 1px;
  background: rgb(71, 85, 105);
  opacity: 0.55;
}

.heat-zone__meta {
  display: flex;
  gap: 0.4em;
  font-size: 0.72em;
  color: rgb(100, 116, 139);
}

.heat-zone__word {
  text-transform: lowercase;
}

.heat-zone__detail::before {
  content: '·';
  margin-right: 0.4em;
}

.heat-note {
  margin: 0;
  font-size: 0.75em;
  color: rgb(100, 116, 139);
}

.heat-note--warn {
  color: rgb(180, 83, 9);
}

.heat-empty {
  font-size: 0.8em;
  color: rgb(100, 116, 139);
  text-align: center;
}

.dark .heat-zone {
  background: rgb(30, 41, 59);
}

.dark .heat-zone__name,
.dark .heat-zone__value {
  color: rgb(226, 232, 240);
}

/* ── Card face ─────────────────────────────────────────────────────────────── */
/* The dial is the card, so the frame gives it its edges back — the same recipe
   the thermostat's gauge card uses. */
.heat-card-face {
  --card-media: 13.5rem;
  --card-pad: 0.3rem 0.3rem 0.35rem;
  gap: 0.15rem;
}

.heat-card-face.device-card--mid {
  --card-media: 9rem;
}

.heat-card-face.device-card--min {
  --card-media: 5.5rem;
}

/* The ring's box: an inscribed square in the media area. Its own font-size is
   what sizes the flame — `ControlIcon` defaults to `1em`, and the shell's
   `--card-media` moves with the page's size toggle. */
.heat-card-dial {
  height: 100%;
  max-width: 100%;
  aspect-ratio: 1;
  margin: 0 auto;
  font-size: calc(var(--card-media, 13.5rem) * 0.3);
}

/* The ring is the gauge, not the frame: a hairline against the disc, so a full
   arc never merges with a lit circle into one orange blob. */
.heat-card-dial :deep(.status-ring__arc) {
  stroke-width: 5;
}

/* Two states, one circle (owner, 03.11.2026: *"its inside a circle which is
   always orange bg.. icon cant be seen"*): unlit it is an orange outline on a
   wash of its own tint, lit it is the solid orange the contact's tone has always
   been — and the flame flips to white on it. */
.heat-card-dial__disc {
  display: grid;
  place-items: center;
  height: 66%;
  max-width: 66%;
  aspect-ratio: 1;
  border-radius: 9999px;
  border: 1px solid color-mix(in srgb, currentColor 40%, transparent);
  background: color-mix(in srgb, currentColor 13%, transparent);
  color: var(--device-color-heat, rgb(249, 115, 22));
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.heat-card-dial--on .heat-card-dial__disc {
  border-color: var(--device-color-heat, rgb(249, 115, 22));
  background: var(--device-color-heat, rgb(249, 115, 22));
  color: #fff;
}

/* Told to stay out: a dashed edge and no tone, so "blocked" never reads as a
   demand that merely happens to be off — even while a contact the operator has
   just blocked is still closed (the handler drops it when the band releases). */
.heat-card-dial--blocked .heat-card-dial__disc {
  border-style: dashed;
  border-color: color-mix(in srgb, rgb(148, 163, 184) 60%, transparent);
  background: rgba(148, 163, 184, 0.14);
  color: rgb(148, 163, 184);
}

.heat-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--card-ink, #64748b);
}

.heat-card-face__value--on {
  color: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 70%, #111827);
}

.dark .heat-card-face__value--on {
  color: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 60%, #f8fafc);
}

.heat-card-face__fact {
  overflow: hidden;
  padding: 0 0.3rem;
  border-radius: 9999px;
  background: color-mix(in srgb, currentColor 14%, transparent);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.heat-card-face__switch {
  display: inline-flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 18%, transparent);
  padding: 0.3rem 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 60%, #111827);
}

.heat-card-face__switch .material-symbols-outlined {
  font-size: 1rem;
}

.heat-card-face__switch--blocked {
  background: rgba(148, 163, 184, 0.18);
  color: #475569;
}

.dark .heat-card-face__switch {
  background: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 22%, transparent);
  color: color-mix(in srgb, var(--device-color-heat, rgb(249, 115, 22)) 45%, #f8fafc);
}

.dark .heat-card-face__switch--blocked {
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}
</style>
